import React, { useEffect } from 'react'
import ActionCard from '../../components/ActionCard'
import ParentStyles from '../../styles'
import NoContent from '~/components/NoContent'
import Styles from './styles'
import { FormattedMessage, useIntl } from 'react-intl'
import { Show } from '~/components/commons'

const extractCardContent = action => {
  let titleTransform = () => {}
  let descriptionTransform = () => {}
  let translationBase = 'actions'
  let redirect = '/'

  switch (action.id) {
    case 'missing-cover-photo':
      translationBase = 'actions.missing-cover-photo'
      titleTransform = translation => {
        return translation
      }
      descriptionTransform = translation => {
        return translation.replace('{{count}}', action.value)
      }
      redirect = '/locations/edit/images'
      break
    case 'missing-profile-photo':
      translationBase = 'actions.missing-profile-photo'
      titleTransform = translation => {
        return translation
      }
      descriptionTransform = translation => {
        return translation.replace('{{count}}', action.value)
      }
      redirect = '/locations/edit/images'
      break
    case 'unanswered-questions':
      translationBase = 'actions.unanswered-questions'
      titleTransform = translation => {
        return translation.replace('{{count}}', action.value)
      }

      descriptionTransform = translation => {
        return translation.replace('{{count}}', action.value)
      }
      redirect = '/locations/questions'
      break
    case 'unanswered-reviews':
      translationBase = 'actions.unanswered-reviews'
      titleTransform = translation => {
        return translation.replace('{{count}}', action.value)
      }
      descriptionTransform = translation => {
        return translation.replace('{{count}}', action.value)
      }
      redirect = '/reviews'
      break
    case 'days-without-posts':
      translationBase = 'actions.days-without-posts'
      const then = new Date(action.value)
      const now = new Date()
      const diffTimeInDays = Math.floor(
        Math.abs(now - then) / (1000 * 60 * 60 * 24)
      )

      titleTransform = translation => {
        return translation.replace('{{count}}', diffTimeInDays)
      }
      descriptionTransform = translation => {
        return translation
      }
      redirect = '/posts/event'
      break
    default:
      translationBase = 'actions.holiday'
      titleTransform = (translation, holiday) => {
        return translation.replace('{{holiday}}', holiday)
      }
      descriptionTransform = (translation, holiday) => {
        return translation
          .replace('{{holiday}}', holiday)
          .replace('{{count}}', action.value?.locations)
      }
      redirect = '/locations/edit/general'
      break
  }
  return {
    titleTransform,
    translationBase,
    descriptionTransform,
    redirect,
  }
}

function CardStructure({ action, onIgnore = () => {} }) {
  const intl = useIntl()
  const variant = extractVariant(action.id)
  const {
    titleTransform,
    translationBase,
    descriptionTransform,
    redirect,
  } = extractCardContent(action)
  const title = titleTransform(
    intl.formatMessage({ id: `${translationBase}.title` }),
    intl.formatMessage({
      id: `holidays.${action.value?.holiday || ''}`,
    })
  )
  const description = descriptionTransform(
    intl.formatMessage({ id: `${translationBase}.description` }),
    intl.formatMessage({
      id: `holidays.${action.value?.holiday || ''}`,
    })
  )
  const buttonText = intl.formatMessage({
    id: `${translationBase}.button`,
  })
  const ignorable = true
  const badgeContent = intl.formatMessage({ id: `homeActionsBadge.${variant}` })

  const handleIgnoreClick = () => {
    onIgnore(action.id)
  }

  return (
    <ActionCard variant={variant} className="action-card">
      <ActionCard.ContentWrapper>
        <ActionCard.Badge>{badgeContent}</ActionCard.Badge>
        <ActionCard.TextWrapper>
          <ActionCard.Title>{title}</ActionCard.Title>
          <ActionCard.Description>
            <span dangerouslySetInnerHTML={{ __html: description }} />
          </ActionCard.Description>
        </ActionCard.TextWrapper>
      </ActionCard.ContentWrapper>
      <ActionCard.ButtonsWrapper>
        <a href={redirect}>
          <ActionCard.Button>{buttonText}</ActionCard.Button>
        </a>
        {ignorable && (
          <ParentStyles.OutlineButton onClick={handleIgnoreClick}>
            <FormattedMessage id={'homeActionsButton.ignore'} />
          </ParentStyles.OutlineButton>
        )}
      </ActionCard.ButtonsWrapper>
    </ActionCard>
  )
}

function ActionsSection({ recommendedActions = [] }) {
  const { bodyRef, isBottomVisible, isTopVisible } = useActionsBlur()
  const [ignoreActions, setIgnoreActions] = useIgnoreActions()
  const refinedActions = [...recommendedActions].filter(
    action => ignoreActions.indexOf(action.id) === -1
  )
  return (
    <Styles.Container>
      <Styles.Header>
        <ParentStyles.Title>
          <FormattedMessage id={'homeActionsTitle'} />
        </ParentStyles.Title>
      </Styles.Header>
      <Styles.Body ref={bodyRef}>
        <Styles.ActionsWrapper>
          <Show condition={!refinedActions || refinedActions?.length === 0}>
            <NoContent>
              <NoContent.Icon />
              <NoContent.Description>
                <FormattedMessage id={'homeActionsNoContent'} />
              </NoContent.Description>
            </NoContent>
          </Show>
          <Show condition={refinedActions && refinedActions?.length > 0}>
            {refinedActions?.map((item, index) => {
              return (
                <CardStructure
                  key={index}
                  action={item}
                  variant={extractVariant(item.id)}
                  title={item.title}
                  description={item.description}
                  buttonText={item.buttonText}
                  onIgnore={setIgnoreActions}
                />
              )
            })}
          </Show>
        </Styles.ActionsWrapper>
      </Styles.Body>
      <div className="bottom-steam steam" data-hide={!isBottomVisible}></div>
      <div className="top-steam steam" data-hide={!isTopVisible}></div>
    </Styles.Container>
  )
}

const useIgnoreActions = () => {
  const [state, setState] = React.useState(() => {
    const ignoreObject = localStorage.getItem(`ignore-actions`)
    if (!ignoreObject) return {}
    return JSON.parse(ignoreObject)
  })

  const setLocalStorage = key => {
    const now = new Date()
    const date = new Date(now.setDate(now.getDate() + 30))
    const inNumbers = date.getTime()
    const newState = { ...state, [key]: { expiration: inNumbers } }
    setState(newState)
  }

  useEffect(() => {
    const toSave = { ...state }
    Object.keys(toSave).forEach(key => {
      const expiration = toSave[key].expiration
      const now = new Date().getTime()
      if (now > expiration) {
        delete toSave[key]
      }
    })
    localStorage.setItem(`ignore-actions`, JSON.stringify(toSave))
  }, [state])

  return [Object.keys(state), setLocalStorage]
}

const useActionsBlur = () => {
  const [isBottomVisible, setIsBottomVisible] = React.useState(true)
  const [isTopVisible, setIsTopVisible] = React.useState(false)
  const bodyRef = React.useRef(null)

  const handleScrollEvent = event => {
    const element = event.target
    const { scrollTop, scrollHeight, clientHeight } = element
    if (scrollTop + clientHeight >= scrollHeight - 2) {
      setIsBottomVisible(false)
    } else {
      setIsBottomVisible(true)
    }

    if (scrollTop === 0) {
      setIsTopVisible(false)
    } else {
      setIsTopVisible(true)
    }
  }

  useEffect(() => {
    if (!bodyRef.current) return
    bodyRef.current.addEventListener('scroll', handleScrollEvent)

    return () => {
      if (bodyRef.current) {
        bodyRef.current.removeEventListener('scroll', handleScrollEvent)
      }
    }
  }, [bodyRef.current])

  return {
    bodyRef,
    isBottomVisible,
    isTopVisible,
  }
}

const extractVariant = id => {
  if (id.includes('holiday')) return 'HOLIDAYS'
  return 'GENERAL_ALERTS'
}

export default ActionsSection
